import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LayoutGrid, Zap, Info, LogOut, X, Menu, Wrench, Users, Map, Disc3, ShoppingBag, CalendarDays } from "lucide-react";
import AdminPanel from "./AdminPanel";
import { T } from "../theme";


// `tab` distinguishes the two dashboard views (mirrors BottomNav): the pill
// toggle on the dashboard itself is gone, so these links are the only desktop
// switcher between Collection and My Stage.
const NAV_LINKS = [
  { path: "/dashboard", label: "Collection", Icon: LayoutGrid, tab: "collection" },
  { path: "/dashboard", label: "My Stage",   Icon: Disc3,      tab: "profile"    },
  { path: "/scan",      label: "Scan",       Icon: Zap        },
  { path: "/friends",   label: "Friends",    Icon: Users      },
  { path: "/map",       label: "Map",        Icon: Map        },
  { path: "/events",    label: "Events",     Icon: CalendarDays },
  { path: "/store",     label: "Store",      Icon: ShoppingBag },
  { path: "/about",     label: "About",      Icon: Info       },
];

function Navbar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const user      = JSON.parse(localStorage.getItem("user") || "null");
  const [scrolled,    setScrolled]    = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  const [adminOpen,   setAdminOpen]   = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 18);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Dashboard links carry a `tab` (collection | profile) — navigation and
  // active state must respect it so Collection and My Stage act as two pages
  const goTo = (link) => {
    if (link.tab) navigate(link.path, { state: { tab: link.tab } });
    else navigate(link.path);
  };

  const isLinkActive = (link) => {
    if (location.pathname !== link.path) return false;
    if (!link.tab) return true;
    const onStage = location.state?.tab === "profile";
    return link.tab === "profile" ? onStage : !onStage;
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/login");
  };

  // Hidden on login page
  if (location.pathname === "/login") return null;

  const isLanding  = location.pathname === "/";
  // Public-lite pages keep the navbar on mobile (index.css hides the full app
  // navbar ≤768px) — logged-out visitors on /store need a way around.
  const isPublicLite = isLanding || ["/store", "/events"].includes(location.pathname);
  const linksToShow = isLanding
    ? [
        { path: "/events", label: "Events", Icon: CalendarDays },
        { path: "/store", label: "Store", Icon: ShoppingBag },
        { path: "/about", label: "About", Icon: Info },
      ]
    : NAV_LINKS;

  return (
    <>
      <nav className={`top-navbar${isPublicLite ? " top-navbar--landing" : ""}`} style={{
        position:        "fixed",
        top:             12,
        left:            "50%",
        transform:       "translateX(-50%)",
        zIndex:          100,
        width:           "calc(100% - 32px)",
        maxWidth:        860,
        background:      scrolled
          ? "rgba(10,5,20,0.88)"
          : "rgba(10,5,20,0.65)",
        backdropFilter:  "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border:          scrolled
          ? "1px solid rgba(180,79,255,0.22)"
          : "1px solid rgba(255,255,255,0.08)",
        borderRadius:    50,
        padding:         "0 8px 0 16px",
        display:         "flex",
        alignItems:      "center",
        height:          52,
        gap:             6,
        boxShadow:       scrolled
          ? "0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(180,79,255,0.1)"
          : "0 4px 20px rgba(0,0,0,0.3)",
        transition:      "all 0.25s ease",
      }}>

        {/* Logo */}
        <button
          onClick={() => navigate("/")}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "none", border: "none", cursor: "pointer",
            padding: "0 6px 0 0", flexShrink: 0,
          }}
        >
          <div aria-hidden style={{
            width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
            background: `radial-gradient(circle at 35% 32%, rgba(255,255,255,0.6) 0%, ${T.purple} 55%)`,
            border: "1.5px solid rgba(255,255,255,0.3)",
            boxShadow: `0 0 10px ${T.purple}90`,
          }} />
          <span className="navbar-brand-text" style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700, fontSize: "0.95rem",
            color: T.pink,
            letterSpacing: "0.01em",
            display: isLanding ? "block" : "none",
          }}>
            Kandi Konnect
          </span>
        </button>

        {/* Desktop nav links */}
        <div style={{
          display:       "flex",
          alignItems:    "center",
          gap:           2,
          flex:          1,
          justifyContent: isLanding ? "flex-start" : "center",
        }}
          className="navbar-desktop-links"
        >
          {linksToShow.map((link) => {
            const { label, Icon } = link;
            const active = isLinkActive(link);
            return (
              <button
                key={label}
                onClick={() => goTo(link)}
                style={{
                  display:     "flex",
                  alignItems:  "center",
                  gap:         6,
                  padding:     "7px 12px",
                  borderRadius: 50,
                  fontWeight:  active ? 700 : 500,
                  fontSize:    "0.87rem",
                  cursor:      "pointer",
                  transition:  "all 0.18s",
                  background:  active
                    ? "linear-gradient(135deg, rgba(180,79,255,0.28), rgba(255,60,172,0.28))"
                    : "transparent",
                  border: active
                    ? "1px solid rgba(180,79,255,0.35)"
                    : "1px solid transparent",
                  color: active ? T.text : T.muted,
                  boxShadow: active ? "0 2px 12px rgba(180,79,255,0.2)" : "none",
                }}
              >
                <Icon size={14} style={{
                  color: active ? T.purple : T.muted,
                  filter: active ? `drop-shadow(0 0 4px ${T.purple})` : "none",
                }} />
                {label}
              </button>
            );
          })}
        </div>

        {/* Right side */}
        <div className="navbar-auth-actions" style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {isLanding ? (
            /* Landing CTA */
            <button
              onClick={() => navigate(user ? "/dashboard" : "/login")}
              className="navbar-cta-btn"
              style={{
                padding:      "8px 18px",
                borderRadius: 50,
                fontWeight:   700,
                fontSize:     "0.87rem",
                cursor:       "pointer",
                background:   "linear-gradient(135deg, #b44fff, #ff3cac)",
                border:       "none",
                color:        "#fff",
                boxShadow:    "0 0 18px rgba(180,79,255,0.4)",
                transition:   "all 0.18s",
                letterSpacing: "0.01em",
              }}
            >
              {user ? "Dashboard" : "Get Started"}
            </button>
          ) : user ? (
            <>
              {/* Avatar chip */}
              <div style={{
                display:      "flex",
                alignItems:   "center",
                gap:          8,
                padding:      "4px 12px 4px 4px",
                borderRadius: 50,
                background:   "rgba(255,255,255,0.05)",
                border:       "1px solid rgba(255,255,255,0.08)",
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                  background: "linear-gradient(135deg, #b44fff, #ff3cac)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "0.65rem", fontWeight: 800, color: "#fff",
                  letterSpacing: "0.03em",
                }}>
                  {user.username.slice(0, 2).toUpperCase()}
                </div>
                <span style={{ color: T.text, fontSize: "0.85rem", fontWeight: 600 }}>
                  {user.username}
                </span>
              </div>

              {/* Logout */}
              <button
                onClick={handleLogout}
                title="Log out"
                className="navbar-icon-btn"
                style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: "rgba(255,255,255,0.05)",
                  border:     "1px solid rgba(255,255,255,0.08)",
                  color:      T.muted,
                  cursor:     "pointer",
                  display:    "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.18s",
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,60,172,0.15)";
                  e.currentTarget.style.borderColor = "rgba(255,60,172,0.35)";
                  e.currentTarget.style.color = T.pink;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                  e.currentTarget.style.color = T.muted;
                }}
              >
                <LogOut size={14} />
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="navbar-cta-btn"
              style={{
                padding: "8px 18px", borderRadius: 50,
                fontWeight: 700, fontSize: "0.87rem", cursor: "pointer",
                background: "linear-gradient(135deg, #b44fff, #ff3cac)",
                border: "none", color: "#fff",
                boxShadow: "0 0 18px rgba(180,79,255,0.4)",
              }}
            >
              Login
            </button>
          )}

          {/* Admin panel toggle */}
          <button
            onClick={() => setAdminOpen((v) => !v)}
            title="Admin panel"
            className="navbar-icon-btn"
            style={{
              width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
              background: adminOpen ? "rgba(180,79,255,0.2)" : "rgba(255,255,255,0.05)",
              border: adminOpen ? "1px solid rgba(180,79,255,0.45)" : "1px solid rgba(255,255,255,0.08)",
              color: adminOpen ? T.purple : T.muted,
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.18s",
              boxShadow: adminOpen ? "0 0 12px rgba(180,79,255,0.3)" : "none",
            }}
          >
            <Wrench size={14} />
          </button>

          {/* Mobile menu toggle — only on non-landing authenticated pages */}
          {!isLanding && (
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="navbar-mobile-toggle navbar-icon-btn"
              style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "rgba(255,255,255,0.05)",
                border:     "1px solid rgba(255,255,255,0.08)",
                color:      T.muted,
                cursor:     "pointer",
                display:    "none", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {mobileOpen ? <X size={16} /> : <Menu size={16} />}
            </button>
          )}
        </div>
      </nav>

      {/* Mobile dropdown */}
      {mobileOpen && !isLanding && (
        <div
          className="navbar-mobile-menu"
          style={{
            position:    "fixed",
            top:         72,
            left:        16,
            right:       16,
            zIndex:      99,
            background:  "rgba(10,5,20,0.96)",
            backdropFilter: "blur(24px)",
            WebkitBackdropFilter: "blur(24px)",
            border:      "1px solid rgba(180,79,255,0.2)",
            borderRadius: 20,
            padding:     "12px 8px",
            boxShadow:   "0 16px 48px rgba(0,0,0,0.6)",
          }}
        >
          {linksToShow.map((link) => {
            const { label, Icon } = link;
            const active = isLinkActive(link);
            return (
              <button
                key={label}
                onClick={() => goTo(link)}
                style={{
                  display:      "flex",
                  alignItems:   "center",
                  gap:          12,
                  width:        "100%",
                  padding:      "12px 16px",
                  minHeight:    44,
                  borderRadius: 12,
                  fontWeight:   active ? 700 : 500,
                  fontSize:     "0.95rem",
                  cursor:       "pointer",
                  background:   active ? "rgba(180,79,255,0.15)" : "transparent",
                  border:       "none",
                  color:        active ? T.text : T.muted,
                  textAlign:    "left",
                  transition:   "background 0.15s",
                }}
              >
                <Icon size={16} color={active ? T.purple : T.muted} />
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Admin panel */}
      {adminOpen && <AdminPanel onClose={() => setAdminOpen(false)} />}

      {/* Responsive styles.
          ≤768px: the top navbar only renders on the landing page (see index.css);
          there it collapses to logo + CTA + wrench with 44px touch targets.
          769–920px: app pages swap the link row for the hamburger dropdown. */}
      <style>{`
        @media (max-width: 768px) {
          .navbar-desktop-links { display: none !important; }
          .navbar-icon-btn      { width: 44px !important; height: 44px !important; }
          .navbar-cta-btn       { min-height: 44px !important; }
          .navbar-brand-text    { font-size: 0.85rem !important; }
        }
        @media (max-width: 374px) {
          .navbar-brand-text    { display: none !important; }
        }
        @media (min-width: 769px) and (max-width: 920px) {
          .navbar-desktop-links { display: none !important; }
          .navbar-mobile-toggle { display: flex !important; }
        }
      `}</style>
    </>
  );
}

export default Navbar;
